import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'

import { alignQuoteVersionVatV2 } from './quotes-v2.api'
import {
  countSelected,
  defaultSelectedIds,
  selectAlignableLines,
  selectedIds,
} from './vatAlignmentHelpers'
import type { QuoteDraftLinePreview, VatAlignmentDto } from './types'

interface VatAlignmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  quoteId: string
  versionId: string
  lines: QuoteDraftLinePreview[]
  alignment: VatAlignmentDto
  onAlignedSuccess: () => void | Promise<void>
}

// SOLY-VAT-001: selection dialog for the bulk VAT alignment. Lists eligible mismatching lines
// (filtered by `selectAlignableLines`), all pre-checked by default. The user un-ticks any
// intentional exceptions (e.g. service at 21% in a 6% renovation devis) before submitting.
// The bulk endpoint runs the update + RecalculateTotals in one transaction.
export function VatAlignmentDialog({
  open,
  onOpenChange,
  projectId,
  quoteId,
  versionId,
  lines,
  alignment,
  onAlignedSuccess,
}: VatAlignmentDialogProps) {
  const targetVat = alignment.projectDefaultVatPercent
  const proposedLines = useMemo(
    () => selectAlignableLines(lines, alignment),
    [lines, alignment]
  )

  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Synchronous guard against double-submit — React state updates are batched and async,
  // a fast double-click could fire two concurrent POSTs before `isSubmitting` propagates.
  const inFlightRef = useRef(false)

  // Reset selection each time the dialog opens — keeps the default "all proposed" state fresh
  // and avoids stale selection from a previous open.
  useEffect(() => {
    if (open) {
      setSelected(defaultSelectedIds(proposedLines))
    }
  }, [open, proposedLines])

  const selectedCount = countSelected(selected)
  const allChecked = proposedLines.length > 0 && selectedCount === proposedLines.length
  const someChecked = selectedCount > 0 && selectedCount < proposedLines.length

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelected(defaultSelectedIds(proposedLines))
    } else {
      setSelected({})
    }
  }

  const toggleOne = (lineId: string, checked: boolean) => {
    setSelected((prev) => ({ ...prev, [lineId]: checked }))
  }

  const handleSubmit = async () => {
    if (targetVat === null) return
    const ids = selectedIds(selected)
    if (ids.length === 0) return
    if (inFlightRef.current) return

    inFlightRef.current = true
    setIsSubmitting(true)
    try {
      const result = await alignQuoteVersionVatV2(projectId, quoteId, versionId, {
        targetVatPercent: targetVat,
        lineIds: ids,
      })
      // Refetch BEFORE closing the dialog so the banner reflects fresh state on
      // dialog close — avoids the "old counters flash" with slow connections.
      await onAlignedSuccess()
      toast.success(
        `${result.updatedLinesCount} ${result.updatedLinesCount > 1 ? 'lignes alignées' : 'ligne alignée'} sur ${targetVat}%`
      )
      onOpenChange(false)
    } catch (err) {
      console.error(err)
      toast.error("Erreur lors de l'alignement de la TVA")
    } finally {
      inFlightRef.current = false
      setIsSubmitting(false)
    }
  }

  // Defensive: dialog should never render when project has no default VAT
  if (targetVat === null) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="quote-vat-alignment-dialog">
        <DialogHeader>
          <DialogTitle>Aligner la TVA des lignes sur {targetVat}%</DialogTitle>
          <DialogDescription>
            Sélectionne les lignes à aligner sur le régime TVA du projet. Décoche les lignes qui
            doivent conserver leur TVA actuelle.
          </DialogDescription>
        </DialogHeader>

        {proposedLines.length === 0 ? (
          <div className="rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">
            Aucune ligne mismatching à proposer.
          </div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto rounded-md border">
            <div className="sticky top-0 z-10 flex items-center gap-3 border-b bg-background px-3 py-2 text-sm font-medium">
              <Checkbox
                checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                onCheckedChange={(v) => toggleAll(v === true)}
                aria-label="Tout sélectionner"
                data-testid="quote-vat-alignment-toggle-all"
              />
              <span>
                {selectedCount} / {proposedLines.length} sélectionnée(s)
              </span>
            </div>
            <ul className="divide-y">
              {proposedLines.map((line) => (
                <li key={line.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <Checkbox
                    id={`vat-align-line-${line.id}`}
                    checked={Boolean(selected[line.id])}
                    onCheckedChange={(v) => toggleOne(line.id, v === true)}
                    data-testid={`quote-vat-alignment-line-${line.id}`}
                  />
                  <label
                    htmlFor={`vat-align-line-${line.id}`}
                    className="flex flex-1 cursor-pointer items-center justify-between gap-3"
                  >
                    <span className="truncate">{line.label || '(sans titre)'}</span>
                    <span className="text-xs text-muted-foreground">
                      {line.vatPercent}% → {targetVat}%
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            data-testid="quote-vat-alignment-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedCount === 0}
            data-testid="quote-vat-alignment-submit-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Alignement…
              </>
            ) : (
              <>Aligner les {selectedCount} ligne(s) sélectionnée(s)</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
